import {
  JobApplicationStatus,
  JobApplicationStatusNote,
} from '../models/job-application-status.model';
import { JobApplication } from '../models/job-application.model';
import { Alert, Timeline, Tag, Typography, Select } from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  SaveOutlined,
  BellOutlined,
} from '@ant-design/icons';
import React, { useState, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import { Header, Button } from '../../../../../core/components';
import { JobApplicationStagesService } from '../services/job-application-stages-service';
import { UtilService } from '../../../../../core/services/util.service';
import { StagesService } from '../../company/services/stages.service';
import { StageId } from '../../company/models/stages.model';
import { StageWithTemplates } from '../../company/models/templates.model';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import StageNotesTable, { StageNotesTableRef } from './StageNotesTable';
import { prepareNotificationTemplateForEditing } from '../utils/candidate-notification-template';
const { Text } = Typography;

interface Props {
  data: JobApplicationStatus[];
  applicationId: string;
  statuses: string[];
  onAddStage?: () => void;
  clientId?: string;
  companyId?: string;
  application: JobApplication;
}

const StagesTimeline: React.FC<Props> = ({
  data,
  applicationId,
  statuses,
  onAddStage,
  clientId,
  companyId,
  application,
}) => {
  const jobApplication = {
    candidateName: application.fullname || '',
    jobUrl: application.job?.url || '',
    jobApplicationUrl: (application.job as any)?.url || '',
    jobTitle: application.job?.name || '',
    companyName: application.job?.owner?.companyName || '',
    recruiterName:
      application.job?.owner?.fullname || application.ownerFullname || '',
    recruiterEmail: application.job?.owner?.email || '',
    clientName: application.job?.client?.name || '',
    notes: '',
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedStatusForNotes, setSelectedStatusForNotes] =
    useState<JobApplicationStatus | null>(null);
  const [status, setStatus] = useState<JobApplicationStatus>(
    new JobApplicationStatus()
  );
  const [stages, setStages] = useState<StageId[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<StageWithTemplates | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isNotePreviewModalOpen, setIsNotePreviewModalOpen] = useState(false);
  const [selectedNoteForPreview, setSelectedNoteForPreview] =
    useState<JobApplicationStatusNote | null>(null);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [selectedStatusForAddNote, setSelectedStatusForAddNote] =
    useState<JobApplicationStatus | null>(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const stageNotesTableRef = useRef<StageNotesTableRef>(null);

  useEffect(() => {
    const fetchStages = async () => {
      if (!companyId || !clientId) return;

      setLoadingStages(true);
      try {
        const params = {
          filter: 'deleted:eq:false',
        };
        const result = await StagesService.GetStages(
          companyId,
          clientId,
          params
        );
        if (result && result.content) {
          setStages(result.content);
        }
      } catch (error) {
        console.error('Failed to fetch stages:', error);
        setStages([]);
      } finally {
        setLoadingStages(false);
      }
    };

    fetchStages();
  }, [companyId, clientId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const parseNoteContent = (content: string): string => {
    try {
      const parsed = JSON.parse(content);
      if (typeof parsed === 'object' && parsed.content) {
        return parsed.content;
      }
      return typeof parsed === 'string' ? parsed : content;
    } catch {
      return content;
    }
  };

  const stripHtmlTags = (html: string): string => {
    return html.replace(/<[^>]*>/g, '');
  };

  const getPlainTextContent = (content: string): string => {
    const parsedContent = parseNoteContent(content);
    return stripHtmlTags(parsedContent);
  };

  const truncateText = (text: string, maxLength: number = 80): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const sortedData = [...data].sort(
    (a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const quillToolbar = [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ];

  const hasMessageContent = (note?: string): boolean =>
    getPlainTextContent(note ?? '').trim().length > 0;

  const canEditCandidateMessage =
    !status.id && Boolean(selectedTemplateId) && Boolean(status.statusName);

  const renderMessageComposer = () => {
    if (!canEditCandidateMessage) {
      return (
        <ReactQuill
          theme="snow"
          value={status.note || ''}
          readOnly
          modules={{ toolbar: false }}
          placeholder={
            status.statusName
              ? 'Select a template to load a starter message you can edit before sending.'
              : 'Select a stage first.'
          }
          className="quill-readonly"
        />
      );
    }

    return (
      <div className="space-y-2 text-left">
        <Text
          type="secondary"
          className="text-xs sm:text-sm block leading-relaxed"
        >
          Template text is a starting point only. Edit the message below — what
          you send will not change the stored HTML or WhatsApp templates.
        </Text>
        <ReactQuill
          theme="snow"
          value={status.note}
          onChange={(value: string) =>
            setStatus((prev) => ({
              ...prev,
              note: value,
            }))
          }
          modules={{ toolbar: quillToolbar }}
          placeholder="Edit the message to the candidate..."
          className="quill-custom"
        />
      </div>
    );
  };

  const Validate = async (
    status: JobApplicationStatus,
    Callback: () => Promise<void>,
    notifiable: boolean
  ) => {
    if (status.statusName.length === 0) {
      UtilService.Alert(
        'Important!',
        'info',
        'You need to select a state.',
        '400px'
      );
      return;
    }

    if (status.statusName === 'CUSTOM' && status.note.length === 0) {
      UtilService.Alert(
        'Important!',
        'info',
        'You need to enter a message.',
        '400px'
      );
      return;
    }

    if (notifiable && selectedTemplateId && !hasMessageContent(status.note)) {
      UtilService.Alert(
        'Important!',
        'info',
        'Enter a message to the candidate before notifying.',
        '400px'
      );
      return;
    }

    await Callback();
  };

  const ValidateDropdown = (status: JobApplicationStatus) => {
    if (status.statusName.length === 0) {
      return true;
    }

    if (
      selectedTemplate &&
      selectedTemplate.notificationTemplates &&
      selectedTemplate.notificationTemplates.length > 0
    ) {
      if (!selectedTemplateId || selectedTemplateId.length === 0) {
        return true;
      }
      if (!hasMessageContent(status.note)) {
        return true;
      }
    }

    if (status.statusName === 'CUSTOM' && !hasMessageContent(status.note)) {
      return true;
    }

    return false;
  };

  const Add = async (notifiable: boolean): Promise<void> => {
    await Validate(
      status,
      async () => {
        const selectedStage = stages.find(
          (stage) => stage.name === status.statusName
        );
        if (!selectedStage) {
          UtilService.Alert('Important!', 'info', 'Stage not found.');
          return;
        }
        const notificationId =
          notifiable && selectedTemplateId ? selectedTemplateId : '';
        const useEditedMessage = Boolean(
          notificationId && hasMessageContent(status.note)
        );
        setSavingStage(true);
        try {
          await JobApplicationStagesService.AddStage(
            applicationId,
            selectedStage.id,
            status.note,
            notificationId,
            useEditedMessage
          );
          closeStageModal();
          UtilService.Alert(
            'Stage updated',
            'success',
            notifiable
              ? 'The stage was added and the candidate notification was queued.'
              : 'The application was moved to the selected stage.'
          );
          if (onAddStage) {
            await onAddStage();
          }
        } catch (error) {
          console.error('Failed to add application stage:', error);
          UtilService.Alert(
            'Could not update stage',
            'error',
            'Please check your connection and try again.'
          );
        } finally {
          setSavingStage(false);
        }
      },
      notifiable
    );
  };

  const OnChangeSelect = (name: string, value: string) => {
    if (name === 'statusName' && value) {
      const selectedStage = stages.find((stage) => stage.name === value);

      if (selectedStage) {
        fetchTemplateContent(selectedStage.id, false);
        setSelectedTemplateId('');
      }

      setStatus((prevItem) => ({
        ...prevItem,
        [name]: value,
        note: value === 'CUSTOM' ? prevItem.note : '',
      }));
    } else {
      setStatus((prevItem) => ({
        ...prevItem,
        [name]: value,
      }));
    }
  };

  const OnChangeTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);

    if (selectedTemplate && selectedTemplate.notificationTemplates) {
      const template = selectedTemplate.notificationTemplates.find(
        (t) => t.id === templateId
      );
      if (template) {
        setStatus((prevItem) => ({
          ...prevItem,
          note: prepareNotificationTemplateForEditing(
            template.template || '',
            jobApplication
          ),
        }));
      }
    }
  };

  const handleAddStage = () => {
    setStatus(new JobApplicationStatus());
    setSelectedTemplate(null);
    setSelectedTemplateId('');
    setIsModalOpen(true);
  };

  const closeStageModal = () => {
    if (savingStage) {
      return;
    }
    setStatus(new JobApplicationStatus());
    setSelectedTemplate(null);
    setSelectedTemplateId('');
    setIsModalOpen(false);
  };

  const handleShowNotification = (item: JobApplicationStatus) => {
    setStatus((prev) => ({
      ...item,
      note: item.note || prev.note,
    }));
    if (item.statusName?.toUpperCase() === 'CUSTOM') {
      setSelectedTemplateId('');
    } else {
      setSelectedTemplate(null);
      setSelectedTemplateId('');
    }
    setIsModalOpen(true);
  };

  const handleShowNotes = (item: JobApplicationStatus) => {
    setSelectedStatusForNotes(item);
    setIsNotesModalOpen(true);
  };

  const handleAddNote = (item: JobApplicationStatus) => {
    setSelectedStatusForAddNote(item);
    setNewNoteContent('');
    setIsAddNoteModalOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedStatusForAddNote || !newNoteContent.trim()) {
      return;
    }

    try {
      await JobApplicationStagesService.AddStageNote(
        selectedStatusForAddNote.id,
        newNoteContent.trim()
      );

      setIsAddNoteModalOpen(false);
      setSelectedStatusForAddNote(null);
      setNewNoteContent('');

      UtilService.Alert(
        'Success!',
        'success',
        'Note has been added successfully.'
      );

      if (onAddStage) {
        await onAddStage();
      }
    } catch (error) {
      console.error('Error saving note:', error);
      UtilService.Alert(
        'Error!',
        'error',
        'Failed to add note. Please try again.'
      );
    }
  };

  const handleCloseAddNoteModal = () => {
    setIsAddNoteModalOpen(false);
    setSelectedStatusForAddNote(null);
    setNewNoteContent('');
  };

  const handleCloseNotesModal = () => {
    setIsNotesModalOpen(false);
    setSelectedStatusForNotes(null);
  };

  const handleShowNotePreview = (note: JobApplicationStatusNote) => {
    setSelectedNoteForPreview(note);
    setIsNotePreviewModalOpen(true);
  };

  const handleCloseNotePreviewModal = () => {
    setIsNotePreviewModalOpen(false);
    setSelectedNoteForPreview(null);
  };

  const handleRefreshNotes = async () => {
    if (onAddStage) {
      await onAddStage();
    }
  };

  useEffect(() => {
    setSelectedStatusForNotes((current) => {
      if (!current) {
        return current;
      }
      const updatedStatus = data.find((status) => status.id === current.id);
      return updatedStatus ?? current;
    });
  }, [data]);

  const Statuses = (): any[] => {
    if (stages.length > 0) {
      return stages
        .filter((stage) => stage.name?.toUpperCase() !== 'CUSTOM')
        .map((stage) => ({
          value: stage.name,
          label: stage.name.replace(/_/g, ' '),
        }));
    }

    return [
      ...statuses
        .filter((stageName) => stageName.toUpperCase() !== 'CUSTOM')
        .map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
    ];
  };

  const TemplateOptions = (): any[] => {
    if (!selectedTemplate || !selectedTemplate.notificationTemplates) {
      return [];
    }

    return selectedTemplate.notificationTemplates.map((template) => ({
      value: template.id,
      label: template.name,
    }));
  };

  const fetchTemplateContent = async (
    stageId: string,
    spinner: boolean = true
  ) => {
    if (!companyId || !clientId) return;

    setLoadingTemplates(true);
    try {
      const result = await StagesService.GetById(
        companyId,
        clientId,
        stageId,
        spinner
      );
      if (result) {
        setSelectedTemplate(result);
      }
    } catch (error) {
      console.error('Failed to fetch template content:', error);
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const currentStageName = sortedData[0]?.statusName?.replace(/_/g, ' ');
  const selectedTemplateName = selectedTemplate?.notificationTemplates?.find(
    (template) => template.id === selectedTemplateId
  )?.name;
  const canAddStage =
    Boolean(status.statusName) && !loadingStages && !savingStage;
  const canNotify = canAddStage && !ValidateDropdown(status);

  return (
    <>
      <div className="application-timeline">
        <button
          type="button"
          className="application-timeline__add"
          onClick={handleAddStage}
        >
          <PlusCircleOutlined aria-hidden />
          <span>Add pipeline stage</span>
        </button>

        {sortedData.length === 0 ? (
          <div className="application-timeline__empty">
            <Text type="secondary">No pipeline activity yet.</Text>
          </div>
        ) : (
          <Timeline
            mode="left"
            items={sortedData.map((item) => ({
              color: 'blue',
              children: (
                <article className="application-timeline__item">
                  <div className="application-timeline__item-header">
                    <Tag color="blue" className="m-0">
                      {item.statusName?.replace(/_/g, ' ')}
                    </Tag>
                    <Text type="secondary" className="text-xs">
                      {formatDate(item.createdDate)}
                    </Text>
                  </div>

                  <div className="application-timeline__actions">
                    {item.note ? (
                      <button
                        type="button"
                        className="application-timeline__action"
                        onClick={() => handleShowNotification(item)}
                      >
                        <FileTextOutlined aria-hidden />
                        View notification
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="application-timeline__action"
                      onClick={() => handleAddNote(item)}
                    >
                      <PlusCircleOutlined aria-hidden />
                      Add note
                    </button>
                  </div>

                  {item.jobApplicationStatusNotes?.length > 0 ? (
                    <div className="application-timeline__notes">
                      <div className="application-timeline__notes-label">
                        <CommentOutlined aria-hidden />
                        Recent notes
                      </div>
                      {item.jobApplicationStatusNotes
                        .slice(0, 2)
                        .map((note, noteIndex) => (
                          <button
                            type="button"
                            className="application-timeline__note"
                            key={note.id || noteIndex}
                            onClick={() => handleShowNotePreview(note)}
                          >
                            <span>
                              {note.createdBy?.includes('@')
                                ? note.createdBy.split('@')[0]
                                : note.createdBy}
                            </span>
                            <time>{formatDate(note.createdDate)}</time>
                            <p>
                              {truncateText(
                                getPlainTextContent(note.content),
                                80
                              )}
                            </p>
                          </button>
                        ))}
                      {item.jobApplicationStatusNotes.length > 2 ? (
                        <button
                          type="button"
                          className="application-timeline__view-notes"
                          onClick={() => handleShowNotes(item)}
                        >
                          View all {item.jobApplicationStatusNotes.length} notes
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {item.createdBy ? (
                    <div className="application-timeline__author">
                      <UserOutlined aria-hidden />
                      <span>
                        {item.createdBy.includes('@')
                          ? item.createdBy.split('@')[0]
                          : item.createdBy}
                      </span>
                    </div>
                  ) : null}
                </article>
              ),
            }))}
          />
        )}
      </div>

      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={closeStageModal}
        className="stage-change-modal"
        overlayClassName="stage-change-modal__overlay"
        contentLabel={
          status.id ? 'Candidate notification' : 'Add pipeline stage'
        }
        shouldCloseOnOverlayClick={!savingStage}
        shouldCloseOnEsc={!savingStage}
        ariaHideApp={false}
      >
        <header className="stage-change-modal__header">
          <div className="stage-change-modal__title">
            <span className="stage-change-modal__icon" aria-hidden>
              {status.id ? <FileTextOutlined /> : <PlusCircleOutlined />}
            </span>
            <div>
              <span className="stage-change-modal__eyebrow">
                {status.id ? 'Candidate communication' : 'Pipeline update'}
              </span>
              <h2>{status.id ? 'Notification' : 'Add pipeline stage'}</h2>
              {!status.id && (
                <p>
                  Move this application forward and optionally send a candidate
                  update.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="stage-change-modal__close"
            onClick={closeStageModal}
            disabled={savingStage}
            aria-label="Close add stage modal"
          >
            <CloseCircleOutlined aria-hidden />
          </button>
        </header>

        <div className="stage-change-modal__body">
          {!status.id && currentStageName && (
            <div className="stage-change-modal__current">
              <span>Current stage</span>
              <Tag color="blue">{currentStageName}</Tag>
            </div>
          )}

          <div className="stage-change-modal__form">
            {!status.id && (
              <>
                <section className="stage-change-modal__section">
                  <div className="stage-change-modal__section-heading">
                    <span>1</span>
                    <div>
                      <label htmlFor="application-stage-select">
                        Choose the next stage
                      </label>
                      <p>The application timeline will update immediately.</p>
                    </div>
                  </div>
                  <Select
                    id="application-stage-select"
                    aria-label="Choose the next stage"
                    placeholder={
                      loadingStages ? 'Loading stages…' : 'Select a stage'
                    }
                    className="stage-change-modal__select"
                    value={status.statusName || undefined}
                    onChange={(value) => OnChangeSelect('statusName', value)}
                    disabled={status.id.length !== 0 || loadingStages}
                    loading={loadingStages}
                    options={Statuses()}
                    showSearch
                    optionFilterProp="label"
                  />
                </section>

                <section
                  className={`stage-change-modal__section ${
                    !status.statusName ? 'is-disabled' : ''
                  }`}
                >
                  <div className="stage-change-modal__section-heading">
                    <span>2</span>
                    <div>
                      <label htmlFor="application-template-select">
                        Candidate notification
                        <em>Optional</em>
                      </label>
                      <p>
                        Select a template to personalize a message before
                        sending.
                      </p>
                    </div>
                  </div>
                  <Select
                    id="application-template-select"
                    aria-label="Candidate notification template"
                    placeholder={
                      loadingTemplates
                        ? 'Loading templates…'
                        : 'No notification selected'
                    }
                    className="stage-change-modal__select"
                    value={selectedTemplateId || undefined}
                    onChange={OnChangeTemplateSelect}
                    onClear={() => {
                      setSelectedTemplateId('');
                      setStatus((previous) => ({ ...previous, note: '' }));
                    }}
                    allowClear
                    loading={loadingTemplates}
                    disabled={
                      !status.statusName ||
                      loadingTemplates ||
                      !selectedTemplate ||
                      !selectedTemplate.notificationTemplates ||
                      selectedTemplate.notificationTemplates.length === 0 ||
                      status.id.length !== 0
                    }
                    options={
                      selectedTemplate && selectedTemplate.notificationTemplates
                        ? TemplateOptions()
                        : []
                    }
                  />
                  {status.statusName &&
                    !loadingTemplates &&
                    selectedTemplate?.notificationTemplates?.length === 0 && (
                      <Alert
                        type="info"
                        showIcon
                        message="No notification templates are configured for this stage."
                      />
                    )}
                </section>
              </>
            )}

            {(status.id || selectedTemplateId) && (
              <section className="stage-change-modal__composer">
                <div className="stage-change-modal__composer-heading">
                  <div>
                    <span>Message preview</span>
                    {selectedTemplateName && <Tag>{selectedTemplateName}</Tag>}
                  </div>
                  {!status.id && (
                    <Text type="secondary">
                      Review and edit before sending.
                    </Text>
                  )}
                </div>
                {renderMessageComposer()}
              </section>
            )}
          </div>
        </div>

        {!status.id && (
          <footer className="stage-change-modal__footer">
            <button
              type="button"
              className="stage-change-modal__cancel"
              onClick={closeStageModal}
              disabled={savingStage}
            >
              Cancel
            </button>
            <div>
              <button
                type="button"
                className="stage-change-modal__secondary"
                disabled={!canAddStage}
                onClick={() => Add(false)}
              >
                {savingStage ? 'Saving…' : 'Add stage only'}
              </button>
              <button
                type="button"
                className="stage-change-modal__primary"
                disabled={!canNotify}
                onClick={() => Add(true)}
              >
                <BellOutlined aria-hidden />
                {savingStage ? 'Saving…' : 'Add & notify'}
              </button>
            </div>
          </footer>
        )}
      </ReactModal>

      <ReactModal
        isOpen={isNotesModalOpen}
        onRequestClose={() => {}}
        className="bg-white rounded-lg p-6 max-w-4xl mx-auto shadow-lg w-full scrollbar-hide"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
        ariaHideApp={false}
      >
        <div>
          <div className="hidden md:block">
            <Header
              title={`Notes - ${selectedStatusForNotes?.statusName?.replace(/_/g, ' ')}`}
              buttons={[
                {
                  icon: {
                    value: <CloseCircleOutlined />,
                    size: 'text-lg',
                  },
                  text: {
                    size: 'md',
                    value: 'Close',
                    weight: 500,
                  },
                  color: 'red',
                  tone: 500,
                  onClick: handleCloseNotesModal,
                },
              ]}
            />
          </div>

          <div className="md:hidden">
            <div className="flex flex-col space-y-4 mb-4">
              <div className="text-lg font-semibold text-gray-800">
                Notes - {selectedStatusForNotes?.statusName?.replace(/_/g, ' ')}
              </div>
              <div className="flex space-x-3">
                <button
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                  onClick={handleCloseNotesModal}
                >
                  <CloseCircleOutlined className="mr-2" />
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        <div>
          {selectedStatusForNotes && (
            <StageNotesTable
              ref={stageNotesTableRef}
              data={selectedStatusForNotes.jobApplicationStatusNotes || []}
              statusId={selectedStatusForNotes.id}
              onAddNote={handleRefreshNotes}
            />
          )}
        </div>
      </ReactModal>

      <ReactModal
        isOpen={isNotePreviewModalOpen}
        onRequestClose={handleCloseNotePreviewModal}
        className="bg-white rounded-lg p-6 max-w-lg mx-auto shadow-lg w-full scrollbar-hide"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
        ariaHideApp={false}
      >
        <Header
          title={'Note'}
          buttons={[
            {
              icon: {
                value: <CloseCircleOutlined />,
                size: 'text-lg',
              },
              text: {
                size: 'text-sm',
                value: 'Close',
                weight: 500,
              },
              color: 'red',
              tone: 500,
              onClick: handleCloseNotePreviewModal,
            },
          ]}
        />

        <div className="quill-readonly">
          <ReactQuill
            theme="snow"
            value={parseNoteContent(selectedNoteForPreview?.content || '')}
            readOnly={true}
            modules={{
              toolbar: false,
            }}
            placeholder={'No content available'}
            className="quill-readonly"
          />
        </div>
      </ReactModal>

      <ReactModal
        isOpen={isAddNoteModalOpen}
        onRequestClose={() => {}}
        className="bg-white rounded-lg p-6 max-w-lg mx-auto shadow-lg w-full scrollbar-hide"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
        ariaHideApp={false}
      >
        <Header
          title="Add Note"
          buttons={[
            {
              icon: {
                value: <CloseCircleOutlined />,
                size: 'text-lg',
              },
              text: {
                size: 'text-sm',
                value: 'Close',
                weight: 500,
              },
              color: 'red',
              tone: 500,
              onClick: handleCloseAddNoteModal,
            },
          ]}
        />

        <div>
          <div className="grid grid-cols-1 mb-5 gap-x-5 gap-y-5">
            <div>
              <ReactQuill
                theme="snow"
                value={newNoteContent}
                readOnly={false}
                onChange={(value: string) => setNewNoteContent(value)}
                className="rounded-xl"
                placeholder="Enter note content..."
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Button
            icon={{ value: <SaveOutlined />, size: 'text-lg' }}
            text={{ value: 'Save', weight: 500, size: 'text-sm' }}
            color="blue"
            tone={500}
            disabled={!newNoteContent.trim()}
            onClick={handleSaveNote}
          />
        </div>
      </ReactModal>
    </>
  );
};

export default StagesTimeline;
